import { getApiBaseUrl } from './apiBase';

// Fetch helper with timeout to avoid hanging UI when API is unresponsive
async function fetchWithTimeout(url, options = {}, timeoutMs = 8000) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    const text = await res.text();
    const ct = (res.headers.get('content-type') || '').toLowerCase();
    return { res, text, ct };
  } finally {
    clearTimeout(timeout);
  }
}

function getToken() {
  try { return localStorage.getItem('token'); } catch { return null; }
}

export async function initializeSubscription(planName, email) {
  const token = getToken();
  if (!token) throw new Error('Please sign in to subscribe');
  const primary = getApiBaseUrl();
  const fallbacks = [primary];
  if (!/localhost/.test(primary)) {
    fallbacks.push('http://localhost:5000/api');
  }
  let lastErrorText = 'Initialization failed';
  for (const base of fallbacks) {
    try {
      const isLocaLt = /\.loca\.lt/i.test(base);
      const { res, text, ct } = await fetchWithTimeout(`${base}/subscriptions/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(isLocaLt ? { 'bypass-tunnel-reminder': 'yes' } : {}),
        },
        body: JSON.stringify({ planName, email }),
      }, 20000);
      // If backend returned HTML "Cannot POST", treat as not supported and try next base
      const isHtml = ct.includes('text/html');
      if (isHtml && /Cannot POST/i.test(text)) {
        lastErrorText = text;
        continue;
      }
      if (res.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }
      if (!res.ok) {
        lastErrorText = text || 'Initialization failed';
        continue;
      }
      try {
        return JSON.parse(text);
      } catch (e) {
        lastErrorText = 'Invalid JSON response';
        void e;
        continue;
      }
    } catch (err) {
      lastErrorText = err?.message || 'Initialization failed';
      continue;
    }
  }
  throw new Error(lastErrorText);
}

export async function confirmSubscription(reference, planName) {
  const token = getToken();
  if (!token) throw new Error('Authentication required. Please log in.');
  const primary = getApiBaseUrl();
  const fallbacks = [primary];
  if (!/localhost/.test(primary)) {
    fallbacks.push('http://localhost:5000/api');
  }
  let lastErrorText = 'Confirmation failed';
  for (const base of fallbacks) {
    try {
      const isLocaLt = /\.loca\.lt/i.test(base);
      const { res, text, ct } = await fetchWithTimeout(`${base}/subscriptions/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(isLocaLt ? { 'bypass-tunnel-reminder': 'yes' } : {}),
        },
        body: JSON.stringify({ reference, planName }),
      }, 20000);
      const isHtml = ct.includes('text/html');
      if (isHtml && /Cannot POST/i.test(text)) {
        lastErrorText = text;
        continue;
      }
      if (res.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }
      if (!res.ok) {
        lastErrorText = text || 'Confirmation failed';
        continue;
      }
      try {
        return JSON.parse(text);
      } catch (e) {
        lastErrorText = 'Invalid JSON response';
        void e;
        continue;
      }
    } catch (err) {
      lastErrorText = err?.message || 'Confirmation failed';
      continue;
    }
  }
  throw new Error(lastErrorText);
}

export async function getSubscriptionStats() {
  const token = getToken();
  const base = getApiBaseUrl();
  try {
    const isLocaLt = /\.loca\.lt/i.test(base);
    const { res, text, ct } = await fetchWithTimeout(`${base}/subscriptions/stats`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(isLocaLt ? { 'bypass-tunnel-reminder': 'yes' } : {}),
      },
    }, 8000);
    
    if (!res.ok) {
      let msg = 'Stats failed';
      try {
        const errJson = JSON.parse(text);
        msg = errJson.msg || errJson.message || msg;
      } catch (_) {
        msg = text || msg;
      }
      throw new Error(msg);
    }
    
    return JSON.parse(text);
  } catch (err) {
    console.error('getSubscriptionStats error:', err);
    throw err;
  }
}

export async function getMyActiveSubscription() {
  const token = getToken();
  const res = await fetch(`${getApiBaseUrl()}/subscriptions/my`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function useOneShipment() {
  const token = getToken();
  const res = await fetch(`${getApiBaseUrl()}/subscriptions/use`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Admin
export async function listAllSubscriptions() {
  const token = getToken();
  const res = await fetch(`${getApiBaseUrl()}/subscriptions/all`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function updateSubscription(id, updates) {
  const token = getToken();
  const res = await fetch(`${getApiBaseUrl()}/subscriptions/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}